import { LoaderCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Action } from '@/authorization/action';
import { Subject } from '@/authorization/subject';
import { useAppAbility } from '@/authorization/use-app-ability';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { formatDate } from '@/lib/format';
import type { DdsStatus, TransitDetail } from '@/types/core/transit/transit';
import { getDdsErrorMessage } from './dds-error-message';
import { DdsStatusBadge } from './dds-status-badge';
import { useTransitDdsSend } from './hooks/use-transit-dds-send';
import { TransitDdsHistory } from './transit-dds-history';

export function TransitPurchaseOrderDds({
  status,
  purchaseOrderId,
  transitId,
  canSend,
  submissionUncertain,
  tracking,
}: {
  status: DdsStatus | null;
  purchaseOrderId: number;
  transitId: string;
  canSend: boolean;
  submissionUncertain: boolean;
  tracking: TransitDetail['purchaseOrders'][number];
}) {
  const { t, i18n } = useTranslation('transits');
  const ability = useAppAbility();
  const isAmendment = tracking.ddsCanAmend;
  const { isOpen, setIsOpen, errorMessage, send, isSending } =
    useTransitDdsSend(transitId, purchaseOrderId, isAmendment);
  const currentStatus = status ?? 'pending';
  const isRetry = currentStatus === 'rejected' || currentStatus === 'error';
  const actionLabel = isAmendment
    ? 'amendDds'
    : isRetry
      ? 'resendDds'
      : 'sendDds';
  const canManage = ability.can(Action.Manage, Subject.ProductionDeclaration);

  return (
    <div className='flex flex-col gap-1 px-4 pb-3'>
      <div className='flex flex-wrap items-center gap-2'>
        <DdsStatusBadge showLabel status={currentStatus} />
        {canManage && (canSend || isAmendment) && (
          <Dialog
            onOpenChange={(open) => {
              if (!isSending) {
                setIsOpen(open);
              }
            }}
            open={isOpen}
          >
            <DialogTrigger asChild>
              <Button
                aria-label={t(`${actionLabel}ForPo`, { id: purchaseOrderId })}
                disabled={isSending}
                size='sm'
                type='button'
                variant='outline'
              >
                {t(actionLabel)}
              </Button>
            </DialogTrigger>
            <DialogContent showCloseButton={!isSending}>
              <DialogHeader>
                <DialogTitle>
                  {t(
                    isAmendment
                      ? 'ddsAmendConfirmTitle'
                      : 'ddsSendConfirmTitle',
                  )}
                </DialogTitle>
                <DialogDescription>
                  {t(
                    isAmendment
                      ? 'ddsAmendConfirmDescription'
                      : 'ddsSendConfirmDescription',
                    { id: purchaseOrderId },
                  )}
                </DialogDescription>
              </DialogHeader>
              {errorMessage && (
                <p className='whitespace-pre-wrap break-words' role='alert'>
                  {errorMessage}
                </p>
              )}
              <DialogFooter>
                <DialogClose asChild>
                  <Button disabled={isSending} variant='outline'>
                    {t('cancel', { ns: 'common' })}
                  </Button>
                </DialogClose>
                <Button disabled={isSending} onClick={send}>
                  {isSending && (
                    <LoaderCircle aria-hidden className='animate-spin' />
                  )}
                  {t(
                    isSending
                      ? 'ddsSending'
                      : isAmendment
                        ? 'amendDds'
                        : 'sendDds',
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
      {tracking.ddsLastConsultedAt && (
        <p className='text-muted-foreground text-xs'>
          {t('ddsLastConsultedAt')}{' '}
          <time dateTime={tracking.ddsLastConsultedAt}>
            {formatDate(tracking.ddsLastConsultedAt, {
              locale: i18n.language,
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </time>
        </p>
      )}
      {tracking.ddsRejectionReason && (
        <p className='whitespace-pre-wrap break-words text-destructive text-sm'>
          {t('ddsRejectionReason')} {tracking.ddsRejectionReason}
        </p>
      )}
      {tracking.ddsError && (
        <div className='text-destructive text-sm' role='status'>
          <p className='whitespace-pre-wrap break-words'>
            {getDdsErrorMessage(tracking.ddsError, t)}
          </p>
          <p className='text-muted-foreground text-xs'>
            {t('ddsErrorOccurredAt')}{' '}
            <time dateTime={tracking.ddsError.occurredAt}>
              {formatDate(tracking.ddsError.occurredAt, {
                locale: i18n.language,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </time>
          </p>
        </div>
      )}
      {submissionUncertain && !tracking.ddsError && (
        <p className='text-muted-foreground text-xs' role='status'>
          {t(
            currentStatus === 'pending'
              ? 'ddsSubmissionInProgress'
              : 'ddsSubmissionUncertain',
          )}
        </p>
      )}
      <TransitDdsHistory tracking={tracking} />
    </div>
  );
}
